<template>
  <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">

    <!-- Table -->
    <table class="w-full text-left mb-4">
      <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
        <tr>
          <th class="py-2 px-4">Field</th>
          <th class="py-2 px-4">Original Value</th>
          <th class="py-2 px-4">New Value</th>
          <th class="py-2 px-4">Pending Value</th>
        </tr>
      </thead>
      <tbody class="divide-y dark:divide-gray-600/60">
        <tr
          v-for="(field, index) in displayFields"
          :key="field.key"
          :class="[
            'border-b dark:border-gray-700',
            isFieldEdited(field.key)
              ? 'bg-yellow-50/80 dark:bg-gray-700/60'
              : index % 2 === 0
              ? 'hover:bg-gray-900/30 transition-colors'
              : 'hover:bg-gray-900/30 transition-colors',
          ]"
        >
          <td class="py-4 px-4">{{ field.label }}</td>
          <td class="py-4 px-4">
            {{ formatValue(field.key, props.site[field.key]) }}
          </td>
          <td class="py-4 px-4">
            <div class="flex items-center">
              <template v-if="editingField === field.key">
                <div class="flex items-start gap-2 w-full">
                  <template v-if="field.type === 'select'">
                    <GlassSelect
                      v-model="editValues[field.key]"
                      @blur="resetIfNotSaved(field.key)"
                      :options="field.options"
                      :valueKey="'value'"
                      :labelKey="'label'"
                      class="flex-grow"
                    />
                  </template>
                  <template v-else-if="field.type === 'checkbox'">
                    <div class="flex-grow">
                      <input
                        v-model="editValues[field.key]"
                        @blur="resetIfNotSaved(field.key)"
                        type="checkbox"
                        class="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <div class="flex-grow">
                      <input
                        v-model="editValues[field.key]"
                        :type="field.type"
                        @keyup.enter="handleEdit(field.key)"
                        @focus="clearFieldError(field.key)"
                        @blur="resetIfNotSaved(field.key)"
                        :class="[
                          'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                            errors[field.key]
                            ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        ]"
                      />
                      <FieldError :message="errors[field.key]" class="mt-1"/>
                    </div>
                  </template>
                  <div class="flex pl-2 space-x-2">
                    <button
                      type="button"
                      @click.stop="handleEdit(field.key)"
                      @mousedown.prevent
                      class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      @click.stop="cancelEdit(field.key)"
                      @mousedown.prevent
                      class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </template>
              <template v-else>
                <span
                  :class="['flex-grow', handleNoEdit(field.key) ? 'cursor-pointer' : '']"
                  @click="handleNoEdit(field.key) && startEdit(field.key)"
                >
                  {{ formatValue(field.key, editValues[field.key]) }}
                </span>
                <div class="relative">
                  <button
                    v-if="handleNoEdit(field.key)"
                    @click="startEdit(field.key)"
                    class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    :id=field.key
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      ></path>
                    </svg>
                  </button>
                  <span 
                    v-if="showToolTip && hoveritem === field.key"
                    class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2 bg-gray-800 dark:shadow-lg dark:bg-gray-500 bg-gray-800 text-white text-center"
                      >
                    Change {{field.label}}
                  </span>
                </div>
                
              </template>
            </div>
          </td>
          <td class="py-4 px-4">
            {{ pendingChanges[field.key] ? formatValue(field.key, pendingChanges[field.key].pending) : '-' }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { getOrgById } from "@/services/orgService";
import { getDivisionById } from "@/services/divisionService";
import { useStore } from "@/store";
import { useRoute } from "vue-router";
import FieldError from "../FieldError.vue";
import GlassSelect from "@/components/GlassSelect.vue";

const props = defineProps({
  site: {
    type: Object,
    required: true,
  },
  displayFields: {
    type: Array,
    required: true,
  },
  pendingChanges: {
    type: Object,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update-site"]);
const editingField = ref(null);
const editValues = ref({});
const editedFields = ref(new Set());
const organizationName = ref(null);
const showToolTip = ref(false);
const divisionName = ref(null);
const noEditFields = ref(['name', 'last_modified', 'created_date']);
const canChangeSite = ref(true);

const fetchOrganizationName = async (orgId) => {
  if (!orgId) {
    organizationName.value = "N/A";
    return;
  }
  try {
    const org = await getOrgById(orgId);
    organizationName.value = org.name;
  } catch (error) {
    console.error("Failed to fetch organization name:", error);
    organizationName.value = "redacted";
  }
};

const route = useRoute();

const fetchDivisionName= async (divId) => {
  if (!divId) {
    divisionName.value = "N/A";
    return;
  }
  try {
    const is_staff = useStore.isStaff();
    const schemaName = route.query.client;

    const division = (is_staff && schemaName)
    ? await getDivisionById(divId, schemaName)
    : await getDivisionById(divId);
    
   divisionName.value = division.name;
  } catch (error) {
    console.error("Failed to fetch divisison name:", error);
    divisionName.value = "redacted";
  }
};

const user = () => {
  return useStore.getUser();
};


onMounted(() => {
  if (props.site.org) {
    fetchOrganizationName(props.site.org);
  }

  if (props.site.division){
    fetchDivisionName(props.site.division) 
  }
  // Initialize editValues with current site values
  props.displayFields.forEach((field) => {
    editValues.value[field.key] = props.site[field.key];
  });
});

watch(() => props.site.org,(newOrgId) => {fetchOrganizationName(newOrgId);});

watch(() => props.site.division,(newDivId) => {fetchDivisionName(newDivId)});

watch(
  () => useStore.getUser(),
  async (newUser) => {
    if (newUser) {
      canChangeSite.value = await useStore.hasPermission("Can change site");
    }
  },
  {immediate : true}
);

const startEdit = (key) => {
  editingField.value = key;
  showToolTip.value = false
};

const handleEdit = (key) => {
  if (editValues.value[key] !== props.site[key]) {
    editedFields.value.add(key);
    emit("update-site", { [key]: editValues.value[key] });
  } else {
    editedFields.value.delete(key);
  }
  editingField.value = null;
};

const isFieldEdited = (key) => {
  return editedFields.value.has(key);
};

const handleNoEdit = (key) => {
  if(canChangeSite.value || user()?.is_staff){
    return !noEditFields.value.includes(key);
  }
};

const handleHash = (value) => {
  const unhashby = 5

  if (value.length <= 2 * unhashby) {
    return value;
  }

  let first = value.slice(0,unhashby)
  let last = value.slice(-unhashby)


  let hashed = "*".repeat(value.length - 2*unhashby)
  return first + hashed + last
};

const formatValue = (key, value) => {
  if (!value) return "-";
  if (key === "created_date" || key === "last_modified") {
    const date = new Date(value);
    return date.toLocaleString();
  }

  if (key === "in_maintenance") {
    return value ? "Yes" : "No";
  }

  if (key === "api_key" && value) {
    return handleHash(value)
  }

  if (key == 'org'){
      return organizationName.value
  }

  if (key == 'division'){
      return divisionName.value
  }
  
  return value;
};

const resetIfNotSaved = (key) => {
  // Add a small delay to allow the save button click to process first
  setTimeout(() => {
    if (!editedFields.value.has(key)) {
      editValues.value[key] = props.site[key];
      editingField.value = null;
    }
  }, 100);
};

const cancelEdit = (key) => {
  clearFieldError(key);
  editValues.value[key] = props.site[key];
  editingField.value = null;
};


const clearFieldError = (fieldName) => {
  if ( props.errors[fieldName]) {
    delete props.errors[fieldName];
    delete props.errors['non_field_errors'];
  }
};
</script>
